import { isNumber } from 'radash';
import { AlignedRecordings, Clip, ClipMetadata, DeletedRange } from '../types/clip-types';
import { ClipDeletes } from '../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { isNotFullLengthClip } from './clipUtils';

/**
 * Merges entries in an object where the `bounds[1]` of one entry matches the `bounds[0]` of another.
 *
 * The function processes the `deletes` object by iterating through its entries,
 * merging consecutive entries that have matching bounds, and returns a new object with merged entries.
 *
 * @param {ClipDeletes} deletes - Deletes of a clip
 *
 * @returns {ClipDeletes} - A new object with merged entries. Keys are based on the `bounds[0]` values of the resulting entries.
 *
 * @example
 * const deletes = {
 *   "0.24": {
 *     "bounds": [0.24, 0.56],
 *     "prev_end_time": 0.24,
 *     "next_start_time": 0.56
 *   },
 *   "0.56": {
 *     "bounds": [0.56, 4],
 *     "prev_end_time": 0.56,
 *     "next_start_time": 4
 *   }
 * };
 *
 * const result = mergeDeletes(deletes);
 * // Output:
 * // {
 * //   "0.24": {
 * //     "bounds": [0.24, 4],
 * //     "prev_end_time": 0.24,
 * //     "next_start_time": 4
 * //   }
 * // }
 */

export function mergeDeletes(deletes: ClipDeletes): ClipDeletes {
  const deletesValues = Object.values(deletes);

  // Sort the entries by their bounds[0] to process them in order
  deletesValues.sort((a, b) => a.bounds[0] - b.bounds[0]);

  const merged: DeletedRange[] = [];

  for (let i = 0; i < deletesValues.length; i++) {
    const current = { ...deletesValues[i] };
    let j = i + 1;

    // Track whether every delete folded into this group is a pause optimization, and remember
    // the first non-pause type so the merged range can adopt it (pause merges into manual/AI deletes).
    let allPauseOptimization = current.deleteType === 'PAUSE_OPTIMIZATION';
    let mergedNonPauseType: DeletedRange['deleteType'] =
      current.deleteType === 'PAUSE_OPTIMIZATION' ? undefined : current.deleteType;

    while (j < deletesValues.length && current.bounds[1] >= deletesValues[j].bounds[0]) {
      const next = deletesValues[j];

      // Don't merge if exactly one delete is a FILLER_WORDS_MENU delete; filler words are managed independently.
      const currentIsFillerWordsMenu = current.deleteType === 'FILLER_WORDS_MENU';
      const nextIsFillerWordsMenu = next.deleteType === 'FILLER_WORDS_MENU';

      if (currentIsFillerWordsMenu !== nextIsFillerWordsMenu) {
        break;
      }

      if (next.deleteType !== 'PAUSE_OPTIMIZATION') {
        allPauseOptimization = false;
        if (mergedNonPauseType === undefined) {
          mergedNonPauseType = next.deleteType;
        }
      }

      current.bounds = [current.bounds[0], Math.max(current.bounds[1], next.bounds[1])];
      current.next_start_time = next.next_start_time;
      j++;
    }

    // Keep PAUSE_OPTIMIZATION only when the entire merged range is pause-only; otherwise it behaves as a regular delete.
    current.deleteType = allPauseOptimization ? 'PAUSE_OPTIMIZATION' : mergedNonPauseType;

    if (current.bounds[0] !== current.bounds[1]) {
      merged.push(current);
    }
    i = j - 1;
  }

  return merged.reduce((result, entry) => {
    result[entry.bounds[0]] = entry;
    return result;
  }, {});
}

export const initializeDeletesAndMaxVideoDuration = async ({
  clip,
  recordings,
  fetchVideoDuration
}: {
  clip: Clip;
  recordings: AlignedRecordings[];
  fetchVideoDuration: (src: string) => Promise<number>;
}) => {
  let fetchedMaxVideoDuration: null | number = null;
  let deletes: ClipDeletes | null = null;
  if (isNotFullLengthClip(clip)) {
    if (!isNumber(clip.asset_metadata.maxVideoDuration)) {
      const firstRecording = recordings?.[0];
      const originalVideoUri = clip.content.preview;
      if (firstRecording || originalVideoUri) {
        const videoUri = firstRecording?.preview_url ?? firstRecording?.mp4_url ?? originalVideoUri;
        const duration = await fetchVideoDuration(videoUri);
        fetchedMaxVideoDuration = duration;
      }
    }

    const { start, end, deletes: existingDeletes } = clip.asset_metadata;
    const maxVideoDuration = fetchedMaxVideoDuration ?? clip.asset_metadata.maxVideoDuration;

    const clipMandatoryDeletes: Clip['asset_metadata']['deletes'] = {};
    if (clip.asset_metadata.start > 0) {
      clipMandatoryDeletes[0] = {
        bounds: [0, start],
        prev_end_time: 0,
        next_start_time: start
      };
    }
    if (isNumber(maxVideoDuration) && end < maxVideoDuration) {
      clipMandatoryDeletes[end] = {
        bounds: [end, maxVideoDuration],
        prev_end_time: end,
        next_start_time: maxVideoDuration
      };
    }
    const overlappingDeletes = { ...(existingDeletes ?? {}), ...clipMandatoryDeletes };
    const finalDeletes = mergeDeletes(overlappingDeletes);
    deletes = finalDeletes;
  }
  return {
    maxVideoDuration: fetchedMaxVideoDuration,
    deletes
  };
};
export async function updateIntroOutroDurationsIfNeeded({
  metadata,
  fetchVideoDuration
}: {
  metadata: Partial<Clip['asset_metadata']>;
  fetchVideoDuration: (url: string) => Promise<number>;
}): Promise<{ updatedMetadata: Partial<Clip['asset_metadata']>; hasUpdated: boolean }> {
  const missingIntroDuration = metadata.intro && !metadata.introDuration;
  const missingOutroDuration = metadata.outro && !metadata.outroDuration;

  if (!missingIntroDuration && !missingOutroDuration) {
    return { updatedMetadata: metadata, hasUpdated: false };
  }

  // Create promises for parallel execution
  const promises: Promise<{ type: 'intro' | 'outro'; duration: number }>[] = [];

  if (missingIntroDuration) {
    promises.push(fetchVideoDuration(metadata.intro as string).then(duration => ({ type: 'intro', duration })));
  }

  if (missingOutroDuration) {
    promises.push(fetchVideoDuration(metadata.outro as string).then(duration => ({ type: 'outro', duration })));
  }

  // Execute all promises in parallel with allSettled
  const results = await Promise.allSettled(promises);

  // Process results
  let introDuration: number | undefined;
  let outroDuration: number | undefined;

  for (const result of results) {
    if (result.status === 'fulfilled') {
      if (result.value.type === 'intro') {
        introDuration = result.value.duration;
      } else if (result.value.type === 'outro') {
        outroDuration = result.value.duration;
      }
    }
    // If status is 'rejected', we simply skip that duration (it remains undefined)
  }

  return {
    updatedMetadata: {
      ...metadata,
      ...(missingIntroDuration && introDuration !== undefined && { introDuration }),
      ...(missingOutroDuration && outroDuration !== undefined && { outroDuration })
    },
    hasUpdated: true
  };
}
export const updateIntroOutroDurationHandler = async ({
  clip,
  fetchVideoDuration
}: {
  clip: Clip;
  fetchVideoDuration: (url: string) => Promise<number>;
}) => {
  let finalClip = clip;
  let hasUpdatedIntroOutroDurations = false;
  {
    const { updatedMetadata, hasUpdated } = await updateIntroOutroDurationsIfNeeded({
      metadata: clip.asset_metadata,
      fetchVideoDuration
    });
    hasUpdatedIntroOutroDurations = hasUpdated;
    if (hasUpdatedIntroOutroDurations) {
      finalClip = { ...clip, asset_metadata: updatedMetadata as ClipMetadata };
    }
  }
  return { hasUpdatedIntroOutroDurations, clip: finalClip };
};
export function normalizeClipMetadataConfigForDeleteFlag<T extends Partial<Clip['asset_metadata']>>(metadata: T): T {
  return {
    ...metadata,
    config: null
  };
}
